import { translations, LANGS, DEFAULT_LANG } from "./translations";

const STORAGE_KEY = "jp-lang";
const CODES = LANGS.map((l) => l.code);

let current = DEFAULT_LANG;
const listeners = [];

// Small local helper so this module stays dependency-free.
const each = (list, fn) => Array.prototype.slice.call(list).forEach(fn);

const t = (key) => {
  const dict = translations[current] || {};
  if (typeof dict[key] === "string") return dict[key];

  const fallback = translations[DEFAULT_LANG][key];
  return typeof fallback === "string" ? fallback : "";
};

/**
 * The project titles and the marquee pair a visible text node with a
 * `.slide-up` span whose ::before renders `attr(data-content)` on hover.
 * Both have to move together or the hover copy goes stale.
 */
const setSlideText = (el, value) => {
  const slide = el.querySelector(".slide-up");
  if (slide) slide.setAttribute("data-content", value);

  each(el.childNodes, (node) => {
    if (node.nodeType === Node.TEXT_NODE) node.remove();
  });
  el.appendChild(document.createTextNode(value));
};


const applyTranslations = () => {
  each(document.querySelectorAll("[data-i18n]"), (el) => {
    el.textContent = t(el.getAttribute("data-i18n"));
  });

  each(document.querySelectorAll("[data-i18n-html]"), (el) => {
    el.innerHTML = t(el.getAttribute("data-i18n-html"));
  });

  each(document.querySelectorAll("[data-i18n-slide]"), (el) => {
    setSlideText(el, t(el.getAttribute("data-i18n-slide")));
  });

  // data-i18n-attr="title:some.key, aria-label:other.key"
  each(document.querySelectorAll("[data-i18n-attr]"), (el) => {
    el.getAttribute("data-i18n-attr")
      .split(",")
      .forEach((pair) => {
        const [attr, key] = pair.split(":").map((s) => s.trim());
        if (attr && key) el.setAttribute(attr, t(key));
      });
  });

  // The mail CTA carries the address; subject and body are translated.
  each(document.querySelectorAll("[data-i18n-mailto]"), (el) => {
    const address = el.getAttribute("data-i18n-mailto");
    el.setAttribute(
      "href",
      `mailto:${address}?subject=${encodeURIComponent(
        t("contact.mailSubject")
      )}&body=${encodeURIComponent(t("contact.mailBody"))}`
    );
  });

  document.documentElement.setAttribute("lang", current);
  document.title = t("meta.title");

  each(
    document.querySelectorAll(
      'meta[name="description"], meta[property="og:description"], meta[property="twitter:description"]'
    ),
    (el) => el.setAttribute("content", t("meta.description"))
  );

  each(
    document.querySelectorAll(
      'meta[name="title"], meta[property="og:title"], meta[property="twitter:title"]'
    ),
    (el) => el.setAttribute("content", t("meta.title"))
  );
};

const syncSwitcher = () => {
  each(document.querySelectorAll("[data-lang]"), (btn) => {
    const isActive = btn.getAttribute("data-lang") === current;
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
};

const read = () => {
  // ?lang=it wins, so a link can be shared already in the right language.
  const fromUrl = (
    new URLSearchParams(window.location.search).get("lang") || ""
  ).toLowerCase();
  if (CODES.includes(fromUrl)) return fromUrl;

  let stored = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // private mode / storage disabled — fall through to detection
  }
  if (CODES.includes(stored)) return stored;

  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  return CODES.includes(nav) ? nav : DEFAULT_LANG;
};

const write = (lang) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // nothing to do — the language still applies for this session
  }
};

export const getLang = () => current;

export const onLanguageChange = (fn) => listeners.push(fn);

export const setLanguage = (lang, { persist = true } = {}) => {
  if (!CODES.includes(lang) || lang === current) return;

  current = lang;
  if (persist) write(lang);

  applyTranslations();
  syncSwitcher();
  listeners.forEach((fn) => fn(current));
};

export const initI18n = () => {
  current = read();
  write(current);
  applyTranslations();
  syncSwitcher();

  each(document.querySelectorAll("[data-lang]"), (btn) => {
    btn.addEventListener("click", () => setLanguage(btn.getAttribute("data-lang")));
  });

  return current;
};

export { t };
